import {
  DispatcherIcon,
  DroneIcon,
  OperatorIcon,
  SirenIcon,
  WarningIcon,
} from "@/assets/icons/Icons";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useAlertsPanel } from "@/context/AlertsPanelContext";
import { useAuth } from "@/context/AuthContext";
import { useGroup } from "@/context/GroupContext";
import { useIncidents } from "@/context/IncidentsContext";
import { useSelectedAlert } from "@/context/SelectedAlertContext";
import { usePollingSubscription } from "@/hooks/usePollingSubscription";
import { useClipboard } from "@/hooks/useClipboard";
import { useAppDispatch, useAppSelector } from "@/redux/dispatch";
import { IncidentsState, setCasePositions, setSelectedIncident, setSelectedIncidentGroupId } from "@/redux/slice/incidentSlice";
import { setMeetingStarts } from "@/redux/slice/meetingSlice";
import { RootState } from "@/redux/store/rootReducers";
import api from "@/services/api";
import { incidentsApi } from "@/services/api/incidents";
import { messagesApi } from "@/services/api/message";
import { Incident, MapIncident, Message } from "@/types/incidents";
import { UserType } from "@/types/user";
import { getErrorMessage } from "@/utils/errorHandling";
import { formatIncidentType } from "@/utils/formatIncidentType";
import formatMessageForCopy from "@/utils/formatMessageForCopy";
import { formatUserInfo } from "@/utils/formatUserInfo";
import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { MeetingSessionConfiguration } from "amazon-chime-sdk-js";
import { format, isToday, isYesterday } from "date-fns";
import {
  Check,
  Copy,
  Group,
  MapPin,
  SendHorizonal,
  UserPlus,
  Video,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Checkbox } from '../../components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { ChatActions } from "./ChatActions";
import { AssignFirstResponderDialog } from "./components/AssignFirstResponderDialog";
import { GroupInfoDialog } from "./components/GroupInfoDialog";
import { UngroupDialog } from "./components/UngroupDialog";
import LocationInfoSection from "./LocationInfo";

interface MainChatProps {
  alert: Incident | MapIncident;
}

const formatMessageTime = (dateString: string) => {
  const date = new Date(dateString);

  if (isToday(date)) {
    return `Today, ${format(date, "hh:mm a")}`;
  }

  if (isYesterday(date)) {
    return `Yesterday, ${format(date, "hh:mm a")}`;
  }

  return format(date, "MMM d, yyyy, hh:mm a");
};

const MainChat = ({ alert }: MainChatProps) => {
  const { setIsAlertsPanelOpen, setIsMainChatOpen, setRelocateToAlert } = useAlertsPanel();
  const { setSelectedAlertId } = useSelectedAlert()
  const { isGroupAlert, getGroupId, groupStats } = useGroup();
  const navigate = useNavigate();
  const { fetchIncidents, mapIncidents, markAsReadMessage } = useIncidents();
  const { userDetails, fetchUserDetails } = useAuth();
  const { handleCopy } = useClipboard();

  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const isGroup = isGroupAlert(alert.incidentID);
  const groupId: any = getGroupId(alert.incidentID);

  const [isResolveDialogOpen, setIsResolveDialogOpen] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [isResolveAllDialogOpen, setIsResolveAllDialogOpen] = useState(false);

  const [isStartingMeeting, setIsStartingMeeting] = useState(false);

  const [isAssignResponderDialogOpen, setIsAssignResponderDialogOpen] =
    useState(false);
  const [isGroupInfoDialogOpen, setIsGroupInfoDialogOpen] = useState(false);
  const [isUngroupDialogOpen, setIsUngroupDialogOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const stats = groupId ? groupStats[groupId] : null;

  const meetingManager = useMeetingManager();
  const { isMeetingStart } = useAppSelector((state: RootState) => state.meeting);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
    setTimeout(() => {
      markAsReadMessage(alert?.incidentID, groupId)
    }, 1000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchUserDetails();
      } catch (error) {
        console.error("Failed to fetch user details:", error);
      }
    };

    if (!userDetails) {
      fetchData();
    }
  }, [fetchUserDetails, userDetails]);

  useEffect(() => {
    if (alert?.incidentID) {
      setIsTracking(false);
      setMessages([]);
      setPage(1);
      setHasMoreMessages(true);
      setIsLoadingMessages(true);
      console.log(hasMoreMessages, page);
      const controller = new AbortController();

      const loadInitialMessages = async () => {
        try {
          let response;
          if (isGroup && groupId) {
            response = await messagesApi.getGroupMessages(groupId, 1);
          } else {
            response = await messagesApi.getIncidentMessages(
              alert.incidentID,
              1
            );
          }

          if (response?.data) {
            markAsReadMessage(alert?.incidentID, groupId)
            setMessages(
              response.data.sort(
                (a: Message, b: Message) =>
                  new Date(a.messageTime).getTime() -
                  new Date(b.messageTime).getTime()
              )
            );
            setHasMoreMessages(response.meta.hasNextPage);
            setPage(2);
          }
        } catch (error: unknown) {
          if (error instanceof Error && error.name !== "AbortError") {
            console.error("Failed to fetch messages:", error);
            toast.error("Failed to load messages");
          }
        } finally {
          setIsLoadingMessages(false);
        }
      };

      loadInitialMessages();

      return () => {
        controller.abort();
        dispatch(setCasePositions(null));
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert?.incidentID, alert.groupID]);

  const handleResolveIncident = async () => {
    if (!alert?.incidentID) {
      toast.error("Cannot resolve incident: No incident selected");
      return;
    }

    try {
      setIsResolving(true);

      await incidentsApi.updateIncident(alert.incidentID, {
        resolveStatus: "ISSUE_RESOLVED",
      });

      setIsResolveDialogOpen(false);

      setIsMainChatOpen(false);
      setIsAlertsPanelOpen(true);
      navigate("/alerts");
      toast.success("Incident successfully resolved");
    } catch (error) {
      console.error("Failed to resolve incident:", error);
      toast.error("Failed to resolve incident. Please try again.");
    } finally {
      setIsResolving(false);
    }
  };

  const handleResolveAllIncidents = async () => {
    if (!alert?.groupID) {
      toast.error("Cannot resolve incidents");
      return;
    }

    try {
      setIsResolving(true);
      await incidentsApi.resolveGroup(alert.groupID);

      setIsResolveAllDialogOpen(false);
      setIsMainChatOpen(false);
      setIsAlertsPanelOpen(true);
      navigate("/alerts");
      await fetchIncidents();
      toast.success("Incidents successfully resolved");
    } catch (error) {
      console.error("Failed to resolve incidents:", error);
      toast.error("Failed to resolve incidents. Please try again.");
    } finally {
      setIsResolving(false);
    }
  };


  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || isSending) return;

    setIsSending(true);
    try {
      if (isGroup && groupId) {
        await messagesApi.sendGroupMessage(groupId, message.trim());
      } else {
        await messagesApi.sendMessage(alert.incidentID, message.trim());
      }
      setMessage("");
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  const handleAssignResponders = async (dispatcherIDs: number[]) => {
    try {
      await incidentsApi.assignResponders(
        alert.incidentID,
        dispatcherIDs,
        groupId
      );
      await fetchIncidents();
      toast.success(
        `Successfully assigned ${dispatcherIDs.length} responder${dispatcherIDs.length === 1 ? "" : "s"}`
      );
    } catch (error) {
      console.error("Failed to assign responders:", error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleUngroupSave = async (selectedIncidentIds: number[]) => {
    if (!alert?.groupID) {
      toast.error("Cannot ungroup incidents");
      return;
    }


    try {
      await api.put(`/incidents/group/${alert.groupID}`, {
        incidentIDs: selectedIncidentIds,
      });
      await fetchIncidents();
      toast.success("Successfully ungrouped incidents");

      if (!selectedIncidentIds.includes(alert.incidentID)) {
        setIsLoadingMessages(true);
        setMessages([]);
        const response = await messagesApi.getIncidentMessages(
          alert.incidentID,
          1
        );
        if (response?.data) {
          setMessages(
            response.data.sort(
              (a: Message, b: Message) =>
                new Date(a.messageTime).getTime() -
                new Date(b.messageTime).getTime()
            )
          );
          setHasMoreMessages(response.meta.hasNextPage);
          setPage(2);
        }
      }
      setIsUngroupDialogOpen(false);
    } catch (error) {
      console.error("Failed to ungroup incidents:", error);
      toast.error("Failed to ungroup incidents. Please try again.");
    } finally {
      setIsLoadingMessages(false);
    }
  };


  usePollingSubscription(
    `chat_${alert?.incidentID || groupId}`,
    async () => {
      if (!alert?.incidentID) return null;
      if (isGroup && groupId) {
        return messagesApi.getGroupMessages(groupId, 1);
      } else {
        return messagesApi.getIncidentMessages(alert.incidentID, 1);
      }
    },
    (response) => {
      if (response?.data) {
        const newMessages = response.data;
        setMessages((prevMessages) => {
          const existingIds = new Set(prevMessages.map(m => m.incidentMessageID));
          const messagesToAppend = newMessages.filter((m: Message) => !existingIds.has(m.incidentMessageID));

          if (messagesToAppend.length === 0) return prevMessages;

          return [...prevMessages, ...messagesToAppend].sort(
            (a, b) => new Date(a.messageTime).getTime() - new Date(b.messageTime).getTime()
          );
        });
        markAsReadMessage(alert?.incidentID, groupId);
      }
    },
    3000,
    [alert?.incidentID, isGroup, groupId]
  );
  const dispatch = useAppDispatch();

  const { seletedTrackingId }: IncidentsState = useAppSelector(
    (state: RootState) => state.incidents
  );
  useEffect(() => {
    const loadLocations = async () => {
      if (isTracking) {
        try {
          if (seletedTrackingId) {
            const loc: any = await incidentsApi.getLocationHistory(seletedTrackingId);
            if (loc && loc.data?.length > 0) {
              loc.data.forEach((element: any) => {
                if (element.userLocationDetail) {
                  dispatch(setCasePositions(element.userLocationDetail))
                }
              });
            }
          }
        } catch (error) {
          console.error(error)
        }
      }
      else {
        dispatch(setCasePositions(null));
      }
    }
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTracking])

  useEffect(() => {
    if (mapIncidents && mapIncidents?.length > 0 && isTracking) {
      const latestloc = mapIncidents.find((i) => i.incidentID === seletedTrackingId);
      if (latestloc?.coordinates) {
        dispatch(setCasePositions(latestloc?.coordinates))
      }
    }
    else {
      dispatch(setCasePositions(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapIncidents])

  useEffect(() => {
    if (!alert?.incidentID) {
      setIsMainChatOpen(false);
      setIsAlertsPanelOpen(true);
      navigate("/alerts");
      return;
    }

    setMessages([]);
    setPage(1);
    setHasMoreMessages(true);
  }, [alert?.incidentID, navigate, setIsMainChatOpen, setIsAlertsPanelOpen]);

  const startMeeting = async () => {
    try {
      setIsStartingMeeting(true);
      const response = await messagesApi.joinMeet(alert.incidentID);
      if (!response.data) {
        toast.error("Failed to start meeting");
        setIsStartingMeeting(false);
        return;
      }

      const { Meeting, Attendee } = response.data;
      dispatch(setMeetingStarts(true));

      const meetingSessionConfiguration = new MeetingSessionConfiguration(
        Meeting,
        Attendee
      );

      await meetingManager.join(meetingSessionConfiguration);
    } catch (error) {
      console.error("Failed to start meeting:", error);
      toast.error("Failed to start meeting");
    } finally {
      setIsStartingMeeting(false);
    }

    await meetingManager.start();
  };


  if (!alert) {
    return null;
  }
  return (
    <div className="fixed top-[60px] md:top-[60px] lg:top-0 w-full sm:w-[440px] h-full bg-white dark:bg-black shadow-lg flex flex-col pl-0 ml-0 sm:pl-4 sm:-ml-4">
      <div className="px-4 py-4 flex items-center gap-2 justify-between">
        <div className="flex gap-2">
          <StatusBadges alert={alert} stats={stats} />
          {!isGroupAlert(alert.incidentID) && (
            <button
              onClick={() => setIsResolveDialogOpen(true)}
              className="px-4 py-2 bg-green-600 text-sm text-white rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center gap-2"
            >
              <Check size={20} /> Mark Resolved
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isGroupAlert(alert.incidentID) && (
            <ChatActions
              isGroup={isGroup}
              onMarkResolved={() => setIsResolveDialogOpen(true)}
              onMarkAllResolved={() => setIsResolveAllDialogOpen(true)}
              onUngroup={() => setIsUngroupDialogOpen(true)}
            />
          )}

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    setIsMainChatOpen(false);
                    setIsAlertsPanelOpen(true);
                    dispatch(setSelectedIncident(null));
                    setSelectedAlertId(null);
                    setIsTracking(false);
                    dispatch(setCasePositions(null));
                    dispatch(setSelectedIncidentGroupId(""));
                    navigate("/alerts");
                  }}
                  className="p-2 hover:bg-gray-50 dark:hover:bg-dark rounded-full transition-colors duration-200"
                >
                  {!isMeetingStart && <X size={"24px"} className="text-[#959697]" />}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Close</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="flex items-center px-4 gap-3">
        <div className="text-sub-heading dark:text-gray-200">
          {isGroupAlert(alert.incidentID)
            ? `Group ID: #${getGroupId(alert.incidentID)}`
            : formatUserInfo(alert.ReportedByUser)}{" "}
        </div>
        {!isGroupAlert(alert.incidentID) && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {!isMeetingStart &&
                  <button
                    onClick={startMeeting}
                    disabled={isStartingMeeting}


                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    {isStartingMeeting ? (
                      <LoadingSpinner size="small" />
                    ) : (
                      <Video className="w-5 h-5" />
                    )}
                  </button>
                }
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs ">Start Calling</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => {
                  setRelocateToAlert(null);
                  setTimeout(() => {
                    setRelocateToAlert(alert)
                  }, 0);
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-gray-600 bg-gray-200 hover:bg-gray-300"
              >
                <MapPin className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">Relocate</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex items-center gap-2 mt-2 mb-1 ml-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsAssignResponderDialogOpen(true)}
          className="dark:text-gray-200"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          First Responders
        </Button>
        {isGroupAlert(alert.incidentID) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsGroupInfoDialogOpen(true)}
            className="dark:text-gray-200"
          >
            <Group className="w-4 h-4 mr-2" />
            Group Info
          </Button>
        )}
        {!isGroupAlert(alert.incidentID) && (
          <div className="flex flex-1 items-center mx-4">
            <Checkbox
              checked={isTracking}
              onCheckedChange={() => setIsTracking(!isTracking)}
            />
            <div className="text-normal dark:text-gray-200 pl-2">
              Track Incident
            </div>
          </div>
        )}
      </div>
      <div className="ml-4">
        <div className="flex items-center text-[#05539c]">
          <MapPin size={18} color="#05539c" />{alert?.locationID}
        </div>
      </div>
      {/* Info Section */}
      <LocationInfoSection alert={alert} />

      <div className="flex-1 overflow-y-auto p-4 lg:pb-4 pb-[10vh] mb-[70px]">
        {(() => {
          if (isLoadingMessages && messages.length === 0) {
            return (
              <div className="flex justify-center items-center h-full">
                <LoadingSpinner size="medium" color="rgb(5 83 156)" />
              </div>
            );
          }
          if (messages.length === 0) {
            return (
              <div className="text-center text-gray-500 dark:text-gray-200">
                No messages yet. Start the conversation!
              </div>
            );
          }
          return messages.map((msg) => (
            <MessageBubble
              key={msg.incidentMessageID}
              msg={msg}
              alert={alert}
              handleCopy={handleCopy}
            />
          ));
        })()}
        <div ref={messagesEndRef} />
      </div>
      <div className="p-4 border-t border-[#E3E3E3] bg-[#EFEFEF] dark:bg-dark w-full fixed bottom-0 sm:w-[424px]">
        <div className="flex gap-2">
          <textarea
            placeholder="Write your message"
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (!isSending) handleSendMessage(e);
              }
            }}
            disabled={isSending}
            rows={1}
            className="flex-1 px-4 py-3 border border-[#E3E3E3] rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none overflow-hidden dark:bg-black dark:text-gray-200"
            style={{
              minHeight: "48px",
              maxHeight: "120px",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "transparent transparent",
            }}
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleSendMessage}
                  disabled={isSending}
                  className={`px-3 py-3 ${isSending ? "bg-gray-300 cursor-not-allowed" : "bg-black dark:bg-gray-200 text-white dark:text-dark dark:hover:bg-gray-400 hover:bg-gray-900"} rounded-full transition-colors`}
                >
                  {isSending ? (
                    <LoadingSpinner size="small" color="rgb(0 0 0)" />
                  ) : (
                    <SendHorizonal size={"24px"} />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Send</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <Dialog open={isResolveDialogOpen} onOpenChange={setIsResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dark:text-gray-200">Resolve Incident</DialogTitle>
            <DialogDescription>
              Are you sure you want to mark this incident as resolved?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsResolveDialogOpen(false)}
              disabled={isResolving}
              className="dark:bg-dark dark:text-gray-200"
            >
              Cancel
            </Button>
            <Button onClick={handleResolveIncident} disabled={isResolving}>
              {isResolving ? (
                <LoadingSpinner size="small" color="white" />
              ) : (
                "Confirm Resolve"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isResolveAllDialogOpen}
        onOpenChange={setIsResolveAllDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dark:text-gray-200">Resolve All Incidents</DialogTitle>
            <DialogDescription>
              Are you sure you want to mark all incidents in this group as
              resolved?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsResolveAllDialogOpen(false)}
              disabled={isResolving}
              className="dark:bg-dark dark:text-gray-200"
            >
              Cancel
            </Button>
            <Button onClick={handleResolveAllIncidents} disabled={isResolving}>
              {isResolving ? (
                <LoadingSpinner size="small" color="white" />
              ) : (
                "Confirm Resolve All"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AssignFirstResponderDialog
        open={isAssignResponderDialogOpen}
        onClose={() => setIsAssignResponderDialogOpen(false)}
        onAssign={handleAssignResponders}
        AssignedDispatchers={alert.AssignedDispatchers}
        alert={alert}
      />
      {
        isGroupAlert(alert.incidentID) && (
          <GroupInfoDialog
            open={isGroupInfoDialogOpen}
            onClose={() => setIsGroupInfoDialogOpen(false)}
            groupId={getGroupId(alert.incidentID) || ""}
          />
        )
      }
      <UngroupDialog
        open={isUngroupDialogOpen}
        onClose={() => setIsUngroupDialogOpen(false)}
        groupId={groupId}
        onSave={handleUngroupSave}
      />

    </div >
  );
};
export default MainChat;

const StatusBadges = ({ alert, stats }: { alert: Incident | MapIncident; stats: any }) => {
  const { isGroupAlert } = useGroup();

  if (!isGroupAlert(alert.incidentID)) {
    let badgeClass = "badge-danger";
    let Icon = SirenIcon;

    if (alert?.incidentType === "NEAR_DANGER") {
      badgeClass = "badge-near-danger";
      Icon = WarningIcon;
    } else if (alert?.incidentType === "DRONE_ALERT") {
      badgeClass = "badge-drone";
      Icon = DroneIcon;
    }

    return (
      <div
        className={`flex items-center justify-center gap-2 px-4 text-sm py-2 rounded-[8px] ${badgeClass}`}
      >
        <Icon width={22} height={alert?.incidentType === "DRONE_ALERT" ? 24 : 20} />
        {formatIncidentType(alert?.incidentType)}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {stats && (
        <>
          {stats.dangerCount > 0 && (
            <div
              className="flex items-center justify-center gap-1 px-2 py-2 text-sm font-medium rounded-[8px] badge-danger"
            >
              <SirenIcon width={22} height={22} />
              <span>{stats.dangerCount}</span>
            </div>
          )}
          {stats.droneCount > 0 && (
            <div
              className={`flex items-center justify-center gap-1 px-2 text-sm py-2 rounded-[8px] badge-drone`}
            >
              <DroneIcon width={22} height={22} />
              {stats.droneCount}
            </div>
          )}
          {stats.nearDangerCount > 0 && (
            <div
              className={`flex items-center justify-center gap-1 px-2 text-sm py-2 rounded-[8px] badge-near-danger`}
            >
              <WarningIcon width={22} height={22} />
              {stats.nearDangerCount}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const MessageBubble = ({
  msg,
  alert,
  handleCopy,
}: {
  msg: Message;
  alert: Incident | MapIncident;
  handleCopy: (text: string) => void;
}) => {
  if (msg.userType === UserType.SYSTEM) {
    return (
      <div className="flex flex-col justify-center items-center m-2 ">
        <div className="text-center dark:text-gray-200 bg-[rgba(0,0,0,0.2)] py-0 px-3 border rounded-[10px] message-div">
          <span className="text-xs whitespace-pre-line">
            {msg.message}
          </span>
          <span className="text-xs block">
            {formatMessageTime(msg.messageTime)}
          </span>
        </div>
      </div>
    );
  }

  const isOther = msg.userType === UserType.APP_USER || msg.userType === UserType.DISPATCHER;

  return (
    <div
      className={`flex items-start mb-4 group ${isOther ? "justify-start" : "justify-end"}`}
    >
      {isOther && (
        <div className="w-8 h-8 rounded-full bg-[#05539c] flex items-center justify-center text-white mr-2 self-end">
          {msg.userType === UserType.DISPATCHER ? (
            <DispatcherIcon />
          ) : (
            msg.userName.charAt(0)
          )}
        </div>
      )}
      {!isOther && (
        <CopyToClipboard
          text={formatMessageForCopy(msg)}
          onCopy={() => handleCopy("Message copied to clipboard")}
        >
          <button className="p-2 hover:bg-gray-100 dark:hover:bg-dark rounded-full transition-colors duration-200 opacity-0 group-hover:opacity-100 mr-2 self-center dark:text-gray-200">
            <Copy size={14} className="text-gray-600 dark:text-gray-200" />
          </button>
        </CopyToClipboard>
      )}
      <div
        className={`relative max-w-[300px] min-w-[120px] min-h-[54px] p-2 rounded-lg ${isOther
          ? "bg-[#EFEFEF] rounded-bl-none"
          : "bg-[#BBDEFC] rounded-br-none"
          } message-div`}
      >
        <span className="text-body whitespace-pre-line">
          {msg.message}
        </span>
        <span className="text-xs block text-right">
          {formatMessageTime(msg.messageTime)}
        </span>
      </div>
      {isOther && (
        <CopyToClipboard
          text={formatMessageForCopy(msg, alert.ReportedByUser)}
          onCopy={() => handleCopy("Message copied to clipboard")}
        >
          <button className="p-2 hover:bg-gray-100 dark:hover:bg-dark rounded-full transition-colors duration-200 opacity-0 group-hover:opacity-100 ml-2 self-center ">
            <Copy size={14} className="text-gray-600 dark:text-gray-200" />
          </button>
        </CopyToClipboard>
      )}
      {!isOther && (
        <div className="w-8 h-8 ml-2 rounded-full bg-gradient-to-r from-[#05539c] to-[#1189E6] flex items-center justify-center self-end">
          <OperatorIcon />
        </div>
      )}
    </div>
  );
};
